import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import { adminUserSalesOrderAdminUserOrdersPage_user_salesOrders_items_SalesOrder as AdminSalesOrdersItemType } from '@Admin/AdminUserOrdersPage/__generated__/adminUserSalesOrderAdminUserOrdersPage'
import { salesOrder_salesOrder as SalesOrderType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'
import { ordersList_me_salesOrders_items_SalesOrder as SalesOrdersItemType } from '@Orders/AllOrdersPage/__generated__/ordersList'
import { TeamQuotes_teamQuotes_items as TeamQuote } from '@Teams/TeamDetailsPage/TeamQuotesPanel/__generated__/TeamQuotes'
import { TeamOrders_teamOrders_items_SalesOrder as TeamSalesOrder } from '@Teams/TeamDetailsPage/TeamOrdersPanel/__generated__/TeamOrders'

import { QuoteDetails_QuoteFragment as QuoteDetailsPageQuoteType } from '~/gql/graphql'
import Box from '~/core/Box'
import Text from '~/core/Text'
import Button from '~/core/Button'
import getColor from '~/lib/getColor'
import { ADMIN_PATH, TEAM_PATH } from '~/redux-modules/preferences'

import { MutationOperations } from './logic'

type SubShareOrDeleteQuotePropsType = Pick<
    QuoteDetailsPageQuoteType,
    'id' | 'name' | 'project' | 'user'
>

interface RemoveTeamQuoteOrderModalPropsType {
    quote?:
        | TeamQuote
        | QuoteDetailsPageQuoteType
        | SubShareOrDeleteQuotePropsType
    order?:
        | TeamSalesOrder
        | SalesOrderType
        | SalesOrdersItemType
        | AdminSalesOrdersItemType
    teamId: number
    teamName?: string
    userId: number
    onClose: () => void
}

const RemoveTeamQuoteOrderModal = (
    props: RemoveTeamQuoteOrderModalPropsType,
) => {
    const { quote, order, teamId, teamName, userId, onClose } = props
    const [isRemoving, setIsRemoving] = useState<boolean>(false)
    const navigate = useNavigate()
    const { removeOrder, removeQuote } = MutationOperations()
    const { userId: userIdInParams } = useParams<{ userId?: string }>()
    const quoteOrderType = quote ? 'quote' : 'order'
    const placement = `remove-${quoteOrderType}-modal`
    const name = quote ? quote.name : order?.name
    const additionalNoteText = order ? ', purchaser' : ''
    const modalText = {
        title: `Are you sure you want to remove ${name} from workspace ${teamName}?`,
        description: `This ${quoteOrderType} will no longer be accessible to workspace members.`,
        note: `Note: Quote owner${additionalNoteText} and any collaborators will still have access.`,
    }

    const redirectPath = userIdInParams
        ? `${ADMIN_PATH}/user/${userIdInParams}/team/${teamId}/${quoteOrderType}s`
        : `${TEAM_PATH}/${teamId}/${quoteOrderType}s`

    const onClickRemove = async () => {
        setIsRemoving(true)
        let success = false

        if (quote) {
            success = await removeQuote(quote.id, teamId, userId)
        } else if (order) {
            success = await removeOrder(order.id, order.name, teamId, userId)
        }
        setIsRemoving(false)
        if (success) {
            navigate(redirectPath)
        }
        onClose()
    }

    return (
        <Modal
            bodyStyle={{ padding: '24px 12px 12px 24px' }}
            centered
            closable
            footer={null}
            visible
            onCancel={onClose}
            width="550px"
            maskClosable
        >
            <Box>
                <QuestionCircleOutlined
                    style={{
                        color: getColor('warning'),
                        fontSize: '21px',
                        marginRight: '18px',
                    }}
                />
                <Box direction="col" flex={1}>
                    <Text
                        fontSize="16px"
                        fontWeight={400}
                        color="blackAlpha65"
                        style={{ marginBottom: '8px', width: '90%' }}
                    >
                        {modalText.title}
                    </Text>
                    <Text
                        color="blackAlpha65"
                        style={{
                            marginBottom: '16px',
                        }}
                    >
                        {modalText.description}
                    </Text>
                    <Text
                        color="blackAlpha45"
                        style={{
                            marginBottom: '24px',
                        }}
                    >
                        {modalText.note}
                    </Text>
                    <Box justifyContent="end">
                        <Button
                            onClick={onClose}
                            name={`${placement}-cancel`}
                            placement={placement}
                            data-test-target={`${placement}-cancel`}
                            style={{ marginRight: '8px', marginLeft: 'auto' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            danger
                            onClick={onClickRemove}
                            name={`${placement}-remove-${quoteOrderType}`}
                            placement={placement}
                            data-test-target={`${placement}-remove-${quoteOrderType}`}
                            loading={isRemoving}
                        >
                            Remove
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}

export default RemoveTeamQuoteOrderModal
